import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import utils from '../../utils';
import { HiTrash } from 'react-icons/hi';
import adminServices from '../../services/AdminServices';
import Select from 'react-select';
import { useAdminData } from '../../context/SessionContext';
import FormError from '../feedback/FormError';

function ServiceModal({ action, activeService, onReload, setActiveService }) {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [price, setPrice] = useState('');
    const [approxDuration, setApproxDuration] = useState('');
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [loading, setLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [error, setError] = useState()
    const { categories, setServices } = useAdminData();
    const [activeTab, setActiveTab] = useState(0);

    const tabs = useMemo(() => {
        const baseTabs = ['Info'];
        if (action === 1) {
            return [...baseTabs, 'Delete'];
        }
        return baseTabs;
    }, [action]);

    useEffect(() => {
        if (action === 1 && activeService) {
            setName(activeService.name || '');
            setDescription(activeService.description || '');
            setPrice(String(activeService.price || ''));
            setApproxDuration(activeService.approx_duration || '');

            if (activeService.category) {
                setSelectedCategory({
                    value: activeService.category.id,
                    label: activeService.category.name,
                });
            } else {
                setSelectedCategory(null);
            }
        } else {
            setName('');
            setDescription('');
            setPrice('');
            setApproxDuration('');
            setSelectedCategory(null);
        }
    }, [action, activeService]);

    // Format skills for react-select
    const categoryOptions = useMemo(() => {
        return (categories || []).map((category) => ({
            value: category.id,
            label: category.name,
        }));
    }, [categories]);

    const updateSession = () => {
        adminServices.getServices().then(
            (res) => {
                setServices(res.data || []);
            },
            (err) => {
                console.log('Session update error', err);
            }
        );
    };

    const handleSubmit = (e) => {
        setError()
        e.preventDefault();
        const closeBtn = document.getElementById('service-modal-close-btn');
        setLoading(true);

        const payload = {
            name,
            description: description || null,
            price: price ? parseInt(price, 10) : 0,
            approx_duration: approxDuration || null,
            category: selectedCategory?.value || null,
        };

        let request;
        if (action === 1) {
            request = adminServices.updateService(activeService.id, payload);
        } else {
            request = adminServices.addService(payload);
        }

        request
            .then((res) => {
                const successMsg = action === 1 ? 'Service Updated Successfully' : 'Service Added Successfully';
                toast.success(successMsg);
                setLoading(false);
                onReload?.();
                updateSession();
                closeBtn?.click();
            })
            .catch((err) => {
                toast.error(utils.toastError);
                console.error(err);
                setError(err)
                setLoading(false);
            });
    };

    const handleDelete = () => {
        const closeBtn = document.getElementById('service-modal-close-btn');
        setDeleteLoading(true);

        adminServices.deleteService(activeService.id)
            .then((res) => {
                toast.success('Service Deleted Successfully');
                setDeleteLoading(false);
                onReload?.();
                updateSession();
                closeBtn?.click();
            })
            .catch((err) => {
                toast.error(utils.toastError);
                console.error(err);
                setDeleteLoading(false);
            });
    };

    const changeTab = (idx) => {
        if (action === 1) {
            setActiveTab(idx);
        }
    };

    const clearData = () => {
        setName('');
        setDescription('');
        setPrice('');
        setApproxDuration('');
        setSelectedCategory(null);
        setActiveTab(0);
        setActiveService?.(null);
        setError()
    };

    return (
        <div>
            <label htmlFor="service-modal" id="service-open-btn" className="hidden">
                Add / Update Service
            </label>
            <input type="checkbox" id="service-modal" className="modal-toggle" />

            <div className="modal">
                <div className="modal-box w-8/11 max-w-3xl">
                    <label
                        onClick={clearData}
                        htmlFor="service-modal"
                        id="service-modal-close-btn"
                        className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
                    >
                        ✕
                    </label>

                    <h2 className="text-lg font-bold mb-2">
                        {action === 1 ? 'Edit' : 'Add'}
                        <span> Service{action === 1 && activeService && ':'}</span>
                        {action === 1 && activeService && (
                            <span className="text-gray-500 ml-2">{activeService?.name}</span>
                        )}
                    </h2>

                    <div className="flex justify-center mb-6">
                        <div className="border-gray-200 dark:border-border-dark">
                            <div className="tabs tabs-boxed justify-center">
                                {tabs.map((tab, idx) => (
                                    // eslint-disable-next-line jsx-a11y/anchor-is-valid
                                    <a
                                        key={idx}
                                        className={`tab ${activeTab === idx ? 'tab-active bg-primary text-white rounded-lg' : ''}`}
                                        onClick={() => changeTab(idx)}
                                        role="tab"
                                        aria-selected={activeTab === idx}
                                    >
                                        {tab}
                                    </a>
                                ))}
                            </div>
                        </div>
                    </div>

                    {action === 1 && activeService && (
                        <div className="border border-border-light dark:border-border-dark bg-surface-light dark:bg-surface-dark p-3 shadow-sm transition-all hover:shadow-md rounded-xl mb-6">
                            <div className="space-y-2 text-sm">
                                <p className="font-bold">Overview</p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <p>
                                        <span className="font-bold mr-2">Category:</span>
                                        {activeService.category?.name || '—'}
                                    </p>
                                    <p>
                                        <span className="font-bold mr-2">Price:</span> {utils.toCommas(activeService.price)}
                                    </p>
                                    {activeService.approx_duration && (
                                        <p>
                                            <span className="font-bold mr-2">Duration:</span> {activeService.approx_duration}
                                        </p>
                                    )}
                                    <p>
                                        <span className="font-bold mr-2">Patient Count:</span> {activeService?.patient_count || 0}
                                    </p>
                                    <p>
                                        <span className="font-bold mr-2">Nurse Count:</span> {activeService?.nurse_count || 0}
                                    </p>
                                    {activeService.creation_time && (
                                        <p>
                                            <span className="font-bold mr-2">Created:</span>
                                            {new Date(activeService.creation_time).toLocaleDateString()}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 0 && (
                        <form onSubmit={handleSubmit}>
                            {error && (<FormError error={error}/>)}

                            <div className="form-control mb-3">
                                <label className="label">
                                    <span className="label-text">Name *</span>
                                </label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                    className="input input-bordered w-full"
                                    placeholder="e.g. Diabetic Retinopathy Screening"
                                />
                            </div>

                            <div className="form-control mb-3">
                                <label className="label">
                                    <span className="label-text">Description</span>
                                </label>
                                <textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    className="textarea textarea-bordered w-full"
                                    rows="3"
                                    placeholder="Describe the service..."
                                />
                            </div>

                            <div className="form-control mb-3">
                                <label className="label">
                                    <span className="label-text">Category *</span>
                                </label>
                                <Select
                                    value={selectedCategory}
                                    onChange={setSelectedCategory}
                                    options={categoryOptions}
                                    placeholder="Select a category..."
                                    isClearable
                                    required
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            borderColor: '#d1d5db',
                                            '&:hover': { borderColor: '#9ca3af' },
                                        }),
                                    }}
                                    className=''
                                />
                            </div>

                            <div className="flex flex-col md:flex-row gap-4 mb-3">
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text">Price *</span>
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={price}
                                        required
                                        onChange={(e) => setPrice(e.target.value)}
                                        className="input input-bordered w-full"
                                        placeholder="0"
                                    />
                                </div>

                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text">Approx. Duration (e.g. "30 mins")</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={approxDuration}
                                        onChange={(e) => setApproxDuration(e.target.value)}
                                        className="input input-bordered w-full"
                                        placeholder="e.g. 45 mins"
                                    />
                                </div>
                            </div>

                            {/* Submit Button */}
                            <div className="mt-6">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-sky-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-75"
                                >
                                    {loading ? (
                                        <span>{action === 1 ? 'Updating...' : 'Adding...'}</span>
                                    ) : (
                                        <span>{action === 1 ? 'Update' : 'Add'}</span>
                                    )}
                                </button>
                            </div>
                        </form>
                    )}

                    {activeTab === 1 && (
                        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-border-light dark:border-border-dark overflow-hidden mt-6">
                            <div className="p-6 md:p-8">
                                <div className="border-l-4 border-error pl-4 py-2">
                                    <h3 className="font-bold text-error">Danger Zone</h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-500 mt-2">
                                        Deleting this service is irreversible.
                                    </p>
                                    <div className="pt-4">
                                        {deleteLoading ? (
                                            <div className="flex justify-center py-2">
                                                <span className="loading loading-spinner loading-sm"></span>
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={handleDelete}
                                                className="flex items-center space-x-2 bg-error text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-error/90 transition-colors"
                                            >
                                                <HiTrash className="h-4 w-4" />
                                                <span>Delete: {activeService?.name}</span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ServiceModal;